/*
 * assets/predictive-search.js
 *
 * Logica del overlay de busqueda predictiva renderizado por
 * snippets/predictive-search-overlay.liquid.
 *
 * - Open/close del overlay (data-rm-search-trigger / data-rm-search-close)
 * - Debounce 300ms en input
 * - Fetch /search/suggest.json (resources[type]=product, limit=6)
 * - Cache LRU (5 entries) por query string
 * - AbortController para cancelar fetch previo
 * - Render con DOM API (textContent + createElement, sin innerHTML)
 * - Estados: hint inicial / loading / empty / error / list
 * - Submit del form va al search nativo de Shopify (/search?q=...&type=product)
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var MIN_CHARS = 2;
  var RESULT_LIMIT = 6;
  var CACHE_SIZE = 5;
  var SEARCH_ENDPOINT = '/search/suggest.json';

  var cache = new Map();
  var debounceTimer = null;
  var currentController = null;

  // ---------- LRU cache ----------
  function cacheGet(key) {
    if (!cache.has(key)) return null;
    var value = cache.get(key);
    cache.delete(key);
    cache.set(key, value);
    return value;
  }

  function cacheSet(key, value) {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, value);
    if (cache.size > CACHE_SIZE) {
      var oldest = cache.keys().next().value;
      cache.delete(oldest);
    }
  }

  // ---------- DOM helpers ----------
  function clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function makeMessage(className, text) {
    var p = document.createElement('p');
    p.className = className;
    p.textContent = text;
    return p;
  }

  // ---------- Money formatter ----------
  // Shopify suggest.json devuelve precios como string pre-formateado
  // ('$ 49.900') o como numero en centavos. Soportamos ambos.
  function formatMoney(value) {
    if (value === null || typeof value === 'undefined') return '';
    if (typeof value === 'string') return value;
    if (typeof value !== 'number') return '';
    var amount = value / 100;
    try {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '$' + amount.toLocaleString('es-CO');
    }
  }

  // ---------- State renderers ----------
  function setHint(resultsEl, text) {
    clearChildren(resultsEl);
    resultsEl.appendChild(makeMessage('rm-search-overlay__hint', text));
    resultsEl.setAttribute('aria-busy', 'false');
  }

  function setLoading(resultsEl) {
    clearChildren(resultsEl);
    var wrap = document.createElement('p');
    wrap.className = 'rm-search-overlay__hint rm-search-overlay__hint--loading';

    var spinner = document.createElement('span');
    spinner.className = 'rm-search-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    var text = document.createElement('span');
    text.textContent = 'Buscando productos…';

    wrap.appendChild(spinner);
    wrap.appendChild(text);
    resultsEl.appendChild(wrap);
    resultsEl.setAttribute('aria-busy', 'true');
  }

  function setEmpty(resultsEl, query) {
    clearChildren(resultsEl);
    resultsEl.appendChild(
      makeMessage(
        'rm-search-overlay__empty',
        'Aún no encontramos productos con "' + query + '". Prueba con otra palabra (edad, tipo de material…) o escríbenos por WhatsApp y te ayudamos a elegir.'
      )
    );
    resultsEl.setAttribute('aria-busy', 'false');
  }

  function setError(resultsEl) {
    clearChildren(resultsEl);
    resultsEl.appendChild(
      makeMessage(
        'rm-search-overlay__empty',
        'Algo no salió bien al buscar. Revisa tu conexión, o escríbenos por WhatsApp si necesitas ayuda.'
      )
    );
    resultsEl.setAttribute('aria-busy', 'false');
  }

  function buildProductItem(product) {
    var item = document.createElement('a');
    item.className = 'rm-search-overlay__item';
    item.href = product.url || '#';

    var imgWrap = document.createElement('div');
    imgWrap.className = 'rm-search-overlay__item-img';
    var imgUrl = product.image || product.featured_image || null;
    if (imgUrl) {
      var img = document.createElement('img');
      img.src = imgUrl;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 56;
      img.height = 56;
      imgWrap.appendChild(img);
    }
    item.appendChild(imgWrap);

    var info = document.createElement('div');
    info.className = 'rm-search-overlay__item-info';

    var title = document.createElement('p');
    title.className = 'rm-search-overlay__item-title';
    title.textContent = product.title || '';
    info.appendChild(title);

    var prices = document.createElement('div');
    prices.className = 'rm-search-overlay__item-prices';

    var price = document.createElement('span');
    price.className = 'rm-search-overlay__item-price';
    price.textContent = formatMoney(product.price);
    prices.appendChild(price);

    var hasCompare = product.compare_at_price
      && product.compare_at_price !== product.price
      && Number(product.compare_at_price) > Number(product.price);
    if (hasCompare) {
      var compare = document.createElement('span');
      compare.className = 'rm-search-overlay__item-compare';
      compare.textContent = formatMoney(product.compare_at_price);
      prices.appendChild(compare);
    }

    info.appendChild(prices);
    item.appendChild(info);
    return item;
  }

  function setList(resultsEl, products) {
    clearChildren(resultsEl);
    var ul = document.createElement('ul');
    ul.className = 'rm-search-overlay__list';
    for (var i = 0; i < products.length; i++) {
      var li = document.createElement('li');
      li.appendChild(buildProductItem(products[i]));
      ul.appendChild(li);
    }
    resultsEl.appendChild(ul);
    resultsEl.setAttribute('aria-busy', 'false');
  }

  // ---------- Fetch ----------
  function fetchPredictive(query) {
    if (currentController) currentController.abort();
    currentController = new AbortController();

    var url = SEARCH_ENDPOINT
      + '?q=' + encodeURIComponent(query)
      + '&resources[type]=product'
      + '&resources[limit]=' + RESULT_LIMIT;

    return fetch(url, {
      signal: currentController.signal,
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (data) {
      var products = (data
        && data.resources
        && data.resources.results
        && data.resources.results.products) || [];
      return products;
    });
  }

  function handleQuery(query, resultsEl) {
    var cached = cacheGet(query);
    if (cached) {
      if (cached.length === 0) setEmpty(resultsEl, query);
      else setList(resultsEl, cached);
      return;
    }

    setLoading(resultsEl);

    fetchPredictive(query)
      .then(function (products) {
        cacheSet(query, products);
        if (products.length === 0) setEmpty(resultsEl, query);
        else setList(resultsEl, products);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        setError(resultsEl);
      });
  }

  // ---------- Per-overlay wiring ----------
  function initOverlay(overlay) {
    var input    = overlay.querySelector('[data-rm-search-input]');
    var resultsEl = overlay.querySelector('[data-rm-search-results]');
    var closers  = overlay.querySelectorAll('[data-rm-search-close]');
    var triggers = document.querySelectorAll('[data-rm-search-trigger]');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      overlay.removeAttribute('hidden');
      document.body.classList.add('rm-search-open');
      if (input) setTimeout(function () { input.focus(); }, 80);
    }

    function close() {
      overlay.setAttribute('hidden', '');
      document.body.classList.remove('rm-search-open');
      if (currentController) {
        currentController.abort();
        currentController = null;
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onInput() {
      var q = (input.value || '').trim();
      if (debounceTimer) clearTimeout(debounceTimer);

      if (q.length < MIN_CHARS) {
        if (currentController) {
          currentController.abort();
          currentController = null;
        }
        setHint(resultsEl, 'Empieza a escribir para ver sugerencias…');
        return;
      }

      debounceTimer = setTimeout(function () {
        handleQuery(q, resultsEl);
      }, DEBOUNCE_MS);
    }

    var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      if (overlay.hasAttribute('hidden')) return;

      var focusables = overlay.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;

      var first = focusables[0];
      var last  = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!overlay.contains(active)) {
        // Foco escapó del overlay (caso edge): regresar al primero
        e.preventDefault();
        first.focus();
      }
    }

    triggers.forEach(function (btn) { btn.addEventListener('click', open); });
    closers.forEach(function (btn) { btn.addEventListener('click', close); });
    if (input) input.addEventListener('input', onInput);

    document.addEventListener('keydown', function (e) {
      if (overlay.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') trapFocus(e);
    });
  }

  function init() {
    var overlays = document.querySelectorAll('[data-rm-search-overlay]');
    overlays.forEach(initOverlay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
